import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert, 
  Spinner,
  ListGroup,
  Badge
} from 'react-bootstrap';
import { FaCheck, FaLock, FaCreditCard, FaMoneyBillWave, FaShoppingCart } from 'react-icons/fa';
import { 
  getCourseById, 
  validateAndApplyCourseCode 
} from '../../services/courseService';
import { Course } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const SubscriptionPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [courseCode, setCourseCode] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('code');
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);
  const [subscribeSuccess, setSubscribeSuccess] = useState<string | null>(null);
  
  useEffect(() => {
    if (!currentUser) {
      navigate('/login?redirect=' + encodeURIComponent(`/subscription/${courseId}`));
      return;
    }
    
    if (courseId) {
      fetchCourseDetails();
    }
  }, [courseId, currentUser, navigate]);
  
  const fetchCourseDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!courseId) {
        throw new Error('معرف الكورس غير صحيح');
      }
      
      const courseData = await getCourseById(courseId);
      setCourse(courseData);
      
      // Check if user is already enrolled
      if (currentUser && courseData && courseData.enrolledStudents && courseData.enrolledStudents.includes(currentUser.uid)) {
        setError('أنت مشترك بالفعل في هذا الكورس');
      }
    } catch (error) {
      console.error('Error fetching course details:', error);
      setError('حدث خطأ أثناء تحميل تفاصيل الكورس');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      navigate('/login?redirect=' + encodeURIComponent(`/subscription/${courseId}`));
      return;
    }
    
    try {
      setSubscribeLoading(true);
      setSubscribeError(null);
      setSubscribeSuccess(null);
      
      if (!courseId) {
        throw new Error('معرف الكورس غير صحيح');
      }
      
      if (paymentMethod === 'code') {
        if (!courseCode.trim()) {
          throw new Error('يرجى إدخال كود الكورس');
        }
        
        // validateAndApplyCourseCode es una función normal, no un hook de React
        await validateAndApplyCourseCode(courseCode.trim(), currentUser.uid);
        setSubscribeSuccess('تم الاشتراك في الكورس بنجاح!');
        
        // Redirect to course page after successful subscription
        setTimeout(() => {
          navigate(`/courses/${courseId}`);
        }, 2000);
      } else {
        // Handle other payment methods (placeholder for future implementation)
        setSubscribeError('طريقة الدفع هذه غير متاحة حالياً');
      }
    } catch (error: any) {
      console.error('Error subscribing to course:', error);
      setSubscribeError(error.message || 'حدث خطأ أثناء الاشتراك في الكورس');
    } finally {
      setSubscribeLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
        <p className="mt-2">جاري تحميل تفاصيل الكورس...</p>
      </div>
    );
  }
  
  if (error || !course) {
    return (
      <Container className="my-5">
        <Alert variant="danger">
          {error || 'لم يتم العثور على الكورس'}
        </Alert>
        <div className="text-center mt-4">
          <Button variant="primary">
            <Link to="/courses" className="text-white text-decoration-none">
              العودة إلى الكورسات
            </Link>
          </Button>
        </div>
      </Container>
    );
  }
  
  return (
    <div className="subscription-page py-5">
      <Container>
        <Row className="justify-content-center">
          <Col lg={8}>
            <div className="text-center mb-5">
              <h1 className="subscription-title mb-3">الاشتراك في الكورس</h1>
              <p className="subscription-subtitle text-muted">
                أكمل عملية الاشتراك للوصول إلى جميع محتويات الكورس
              </p>
            </div>
            
            <Row>
              <Col lg={7}>
                <Card className="subscription-form-card shadow-sm mb-4">
                  <Card.Body>
                    <h4 className="mb-4">طريقة الاشتراك</h4>
                    
                    {subscribeError && (
                      <Alert variant="danger" className="mb-4">
                        {subscribeError}
                      </Alert>
                    )}
                    
                    {subscribeSuccess && (
                      <Alert variant="success" className="mb-4">
                        {subscribeSuccess}
                      </Alert>
                    )}
                    
                    <Form onSubmit={handleSubscribe}>
                      <Form.Group className="mb-4">
                        <Form.Label>اختر طريقة الدفع</Form.Label>
                        <div className="payment-methods">
                          <Card 
                            className={`payment-method-card mb-3 ${paymentMethod === 'code' ? 'selected' : ''}`}
                            onClick={() => setPaymentMethod('code')}
                          >
                            <Card.Body className="d-flex align-items-center">
                              <div className="payment-method-radio me-3">
                                <Form.Check
                                  type="radio"
                                  id="payment-code"
                                  name="paymentMethod"
                                  checked={paymentMethod === 'code'}
                                  onChange={() => setPaymentMethod('code')}
                                />
                              </div>
                              <div className="payment-method-icon me-3">
                                <FaLock size={24} className="text-primary" />
                              </div>
                              <div className="payment-method-details">
                                <h5 className="mb-1">كود الكورس</h5>
                                <p className="mb-0 text-muted">استخدم كود الكورس الذي حصلت عليه من المعلم</p>
                              </div>
                            </Card.Body>
                          </Card>
                          
                          <Card 
                            className={`payment-method-card mb-3 ${paymentMethod === 'card' ? 'selected' : ''}`}
                            onClick={() => setPaymentMethod('card')}
                          >
                            <Card.Body className="d-flex align-items-center">
                              <div className="payment-method-radio me-3">
                                <Form.Check
                                  type="radio"
                                  id="payment-card"
                                  name="paymentMethod"
                                  checked={paymentMethod === 'card'}
                                  onChange={() => setPaymentMethod('card')}
                                  disabled
                                />
                              </div>
                              <div className="payment-method-icon me-3">
                                <FaCreditCard size={24} className="text-secondary" />
                              </div>
                              <div className="payment-method-details">
                                <h5 className="mb-1">بطاقة ائتمان</h5>
                                <p className="mb-0 text-muted">الدفع باستخدام بطاقة ائتمان (غير متاح حالياً)</p>
                              </div>
                            </Card.Body>
                          </Card>
                          
                          <Card 
                            className={`payment-method-card ${paymentMethod === 'wallet' ? 'selected' : ''}`}
                            onClick={() => setPaymentMethod('wallet')}
                          >
                            <Card.Body className="d-flex align-items-center">
                              <div className="payment-method-radio me-3">
                                <Form.Check
                                  type="radio"
                                  id="payment-wallet"
                                  name="paymentMethod"
                                  checked={paymentMethod === 'wallet'}
                                  onChange={() => setPaymentMethod('wallet')}
                                  disabled
                                />
                              </div>
                              <div className="payment-method-icon me-3">
                                <FaMoneyBillWave size={24} className="text-secondary" />
                              </div>
                              <div className="payment-method-details">
                                <h5 className="mb-1">محفظة إلكترونية</h5>
                                <p className="mb-0 text-muted">الدفع باستخدام محفظة إلكترونية (غير متاح حالياً)</p>
                              </div>
                            </Card.Body>
                          </Card>
                        </div>
                      </Form.Group>
                      
                      {paymentMethod === 'code' && (
                        <Form.Group className="mb-4">
                          <Form.Label>كود الكورس</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="أدخل كود الكورس"
                            value={courseCode}
                            onChange={(e) => setCourseCode(e.target.value)}
                            required
                          />
                          <Form.Text className="text-muted">
                            أدخل كود الكورس الذي حصلت عليه من المعلم أو المنصة
                          </Form.Text>
                        </Form.Group>
                      )}
                      
                      <div className="d-grid gap-2">
                        <Button 
                          variant="primary" 
                          type="submit"
                          disabled={subscribeLoading || (paymentMethod === 'code' && !courseCode.trim())}
                          className="subscription-btn"
                        >
                          {subscribeLoading ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              جاري الاشتراك...
                            </>
                          ) : (
                            <>
                              <FaShoppingCart className="me-2" />
                              اشترك الآن
                            </>
                          )}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col lg={5}>
                <Card className="course-summary-card shadow-sm">
                  <Card.Body>
                    <h4 className="mb-3">ملخص الكورس</h4>
                    
                    <div className="course-summary-details">
                      <div className="course-image-container mb-3">
                        {course.imageUrl ? (
                          <img 
                            src={course.imageUrl} 
                            alt={course.title} 
                            className="course-image img-fluid rounded"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.onerror = null;
                              target.src = 'https://via.placeholder.com/300x200?text=كورس';
                            }}
                          />
                        ) : (
                          <img 
                            src="https://via.placeholder.com/300x200?text=كورس" 
                            alt={course.title} 
                            className="course-image img-fluid rounded"
                          />
                        )}
                      </div>
                      
                      <h5 className="course-title mb-2">{course.title}</h5>
                      <p className="course-teacher text-muted mb-3">
                        {course.teacherName || 'معلم المنصة'}
                      </p>
                      
                      <div className="course-price-container mb-4">
                        <h3 className="course-price">{course.price ? `${course.price} ج.م` : 'مجاني'}</h3>
                      </div>
                      
                      <div className="course-features mb-4">
                        <h6 className="mb-3">ما الذي ستحصل عليه:</h6>
                        <ListGroup variant="flush">
                          <ListGroup.Item className="border-0 ps-0 py-2">
                            <FaCheck className="me-2 text-success" />
                            وصول كامل لجميع محتويات الكورس
                          </ListGroup.Item>
                          <ListGroup.Item className="border-0 ps-0 py-2">
                            <FaCheck className="me-2 text-success" />
                            {course.lessonsCount || 0} درس
                          </ListGroup.Item>
                          <ListGroup.Item className="border-0 ps-0 py-2">
                            <FaCheck className="me-2 text-success" />
                            {course.duration || 0} ساعة من المحتوى
                          </ListGroup.Item>
                          <ListGroup.Item className="border-0 ps-0 py-2">
                            <FaCheck className="me-2 text-success" />
                            مرفقات وملفات PDF
                          </ListGroup.Item>
                          <ListGroup.Item className="border-0 ps-0 py-2">
                            <FaCheck className="me-2 text-success" />
                            وصول مدى الحياة
                          </ListGroup.Item>
                        </ListGroup>
                      </div>
                      
                      <div className="course-tags">
                        {course.category && (
                          <Badge bg="primary" className="me-2 mb-2">
                            {course.category}
                          </Badge>
                        )}
                        {course.level && (
                          <Badge bg="info" className="me-2 mb-2">
                            {course.level}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
                
                <div className="text-center mt-3">
                  <Button 
                    variant="link" 
                    className="text-decoration-none"
                  >
                    <Link 
                      to={`/courses/${course.id}`} 
                      className="text-decoration-none"
                    >
                      العودة إلى صفحة الكورس
                    </Link>
                  </Button>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
      
      <style>{`
        .subscription-title {
          font-weight: bold;
        }
        
        .subscription-form-card,
        .course-summary-card {
          border-radius: 10px;
          overflow: hidden;
        }
        
        .payment-method-card {
          cursor: pointer;
          border-radius: 8px;
          transition: all 0.3s ease;
        }
        
        .payment-method-card:hover {
          border-color: #0d6efd;
        }
        
        .payment-method-card.selected {
          border-color: #0d6efd;
          background-color: rgba(13, 110, 253, 0.05);
        }
        
        .course-image {
          width: 100%;
          height: 180px;
          object-fit: cover;
        }
        
        .course-price {
          color: #0d6efd;
          font-weight: bold;
        }
        
        .subscription-btn {
          padding: 12px;
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};

export default SubscriptionPage;
